export interface SeoCategory {
  id: string;
  name: string;
  getSeoTitle: () => string;
  getSeoDescription: () => string;
  getKeywords: () => string;
}

export class DepartmentCategoryModel {
  constructor(
    public id: string,
    public name: string,
    public description: string,
    public icon: string
  ) {}

  getSeoTitle(): string {
    switch (this.id) {
      case '2':
        return '소아과(소아청소년과) 비대면 진료 | 아이 감기·열·기침 진료 - 닥터나우';
      default:
        return `${this.name} 비대면 진료 | 진료과·질환별 원격진료 - 닥터나우`;
    }
  }

  getSeoDescription(): string {
    switch (this.id) {
      case '2':
        return '아이가 아플 때 빠르게 소아과 진료를 받고 싶다면? 닥터나우에서 의사와 실시간 비대면진료 후 비대면 약 처방받아 보세요.';
      default:
        return `${this.name}에 맞는 의사를 빠르게 찾고, 병원 방문 없이 비대면진료로 약 처방까지 받아보세요.`;
    }
  }

  getKeywords(): string {
    switch (this.id) {
      case '2':
        return '소아청소년과, 소아과 비대면 진료, 아기 감기, 기침, 열, 소아과약, 처방';
      default:
        return '비대면진료, 원격진료, 온라인진료, 비대면처방, 약처방, 약배송, 약배달, 진료비';
    }
  }
}

export class PopularDepartmentCategoryModel {
  constructor(
    public id: string,
    public name: string
  ) {}
}

export class PopularTreatmentCategoryModel {
  constructor(
    public id: string,
    public name: string
  ) {}
}

export class TreatmentCategoryModel {
  constructor(
    public id: string,
    public name: string,
    public icon: string,
    private _treatments: {
      id: string;
      name: string;
    }[]
  ) {}

  private getSeoTitle(treatmentId: string): string {
    switch (treatmentId) {
      case '1':
        return '감기·독감 비대면 진료 | 병원 방문 없이 처방받는 닥터나우';
      case '558':
        return '인공눈물 처방 | 안구건조증 비대면 진료 - 닥터나우';
      case '9':
        return '탈모약 비대면 진료 | 피나스테리드·두타스테리드 처방 - 닥터나우';
      case '11':
        return '여드름약 비대면 진료 | 항생제·외용제 빠른 처방 - 닥터나우';
      default:
        return `${this.name} 비대면 진료 | 진료과·질환별 원격진료 - 닥터나우`;
    }
  }

  private getSeoDescription(treatmentId: string): string {
    switch (treatmentId) {
      case '1':
        return '코감기, 인후통, 몸살 증상이 있으신가요? 병원 갈 필요 없이 닥터나우에서 의사와 비대면 진료 후 약 처방받아 보세요.';
      case '558':
        return '눈이 뻑뻑하고 피로하다면? 안과 갈 필요 없이 닥터나우에서 인공눈물, 안구건조증 치료제를 비대면 진료로 빠르게 처방받아보세요.';
      case '9':
        return '피나스테리드, 두타스테리드 등 탈모약을 병원 방문 없이 닥터나우에서 비대면으로 안전하게 처방받으세요.';
      case '11':
        return '여드름으로 고생하고 있다면? 피부과 방문 없이 닥터나우에서 항생제 및 외용제를 비대면 진료로 처방받아보세요.';
      default:
        return `${this.name}에 맞는 의사를 빠르게 찾고, 병원 방문 없이 비대면진료로 약 처방까지 받아보세요.`;
    }
  }

  private getKeywords(treatmentId: string): string {
    switch (treatmentId) {
      case '1':
        return '감기, 독감, 인후통, 해열제, 비대면 처방';
      case '558':
        return '인공눈물, 안구건조증, 눈 피로, 비대면 진료, 안과 처방, 눈 시림, 눈물약';
      case '9':
        return '탈모약, 피나스테리드, 두타스테리드, 탈모 비대면 처방';
      case '11':
        return '여드름약, 여드름 처방, 피부과 비대면 진료, 항생제';
      default:
        return '비대면진료, 원격진료, 온라인진료, 비대면처방, 약처방, 약배송, 약배달, 진료비';
    }
  }

  get treatments(): {
    id: string;
    name: string;
    getSeoTitle: () => string;
    getSeoDescription: () => string;
    getKeywords: () => string;
  }[] {
    return this._treatments.map((treatment) => ({
      id: treatment.id,
      name: treatment.name,
      getSeoTitle: () => this.getSeoTitle(treatment.id),
      getSeoDescription: () => this.getSeoDescription(treatment.id),
      getKeywords: () => this.getKeywords(treatment.id),
    }));
  }
}

export class DiagnosisCategoryInfoModel {
  constructor(
    public popularDepartmentCategories: PopularDepartmentCategoryModel[],
    public popularTreatmentCategories: PopularTreatmentCategoryModel[],
    public departmentCategories: DepartmentCategoryModel[],
    public treatmentCategories: TreatmentCategoryModel[]
  ) {}

  findDepartmentCategoryByCategoryId(categoryId: string): DepartmentCategoryModel | null {
    return this.departmentCategories.find((category) => category.id === categoryId) || null;
  }

  private findTreatmentCategoryByTreatmentId(treatmentId: string): TreatmentCategoryModel | null {
    return (
      this.treatmentCategories.find((category) =>
        category.treatments.some((treatment) => treatment.id === treatmentId)
      ) || null
    );
  }

  findTreatmentByTreatmentId(treatmentId: string): SeoCategory | null {
    const foundCategory = this.findTreatmentCategoryByTreatmentId(treatmentId);

    if (!foundCategory) {
      return null;
    }

    return foundCategory.treatments.find((treatment) => treatment.id === treatmentId) || null;
  }

  getDepartmentCategories(): DepartmentCategoryModel[] {
    return this.departmentCategories;
  }
}
